
import React from 'react';
import { Project } from '@/hooks/useProjects';
import { ProjectRrwebSession } from '@/hooks/useProjectRrwebSessions';
import { ProjectSlide } from '@/hooks/useProjectSlides';
import { ProjectAudioElement } from '@/hooks/useProjectAudio';
import TimelineTrack from './TimelineTrack';

interface TimelineEditorProps {
  project: Project;
  rrwebSessions: ProjectRrwebSession[];
  slides: ProjectSlide[];
  audioElements: ProjectAudioElement[];
  currentTime: number;
  onTimeChange: (time: number) => void;
}

const TimelineEditor = ({ 
  project, 
  rrwebSessions, 
  slides, 
  audioElements, 
  currentTime, 
  onTimeChange 
}: TimelineEditorProps) => {
  const timelineRef = React.useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = React.useState(1);
  const [draggedElement, setDraggedElement] = React.useState<string | null>(null);

  // Mock timeline duration (10 seconds for demo)
  const timelineDuration = 10000; // 10 seconds in ms
  const pixelsPerSecond = 50 * zoom;

  const handleTimelineClick = (event: React.MouseEvent) => {
    if (!timelineRef.current) return;
    
    const rect = timelineRef.current.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const timeMs = (clickX / pixelsPerSecond) * 1000;
    
    onTimeChange(Math.max(0, Math.min(timeMs, timelineDuration)));
  };

  const handleElementDrag = (elementId: string, startDrag: boolean) => {
    setDraggedElement(startDrag ? elementId : null);
  };

  return (
    <div className="h-full flex flex-col bg-editor-dark">
      {/* Timeline Ruler */}
      <div className="h-8 bg-editor-medium border-b border-editor-light relative">
        <div 
          className="h-full relative cursor-pointer"
          style={{ width: `${(timelineDuration / 1000) * pixelsPerSecond}px` }}
          onClick={handleTimelineClick}
        >
          {/* Time markers */}
          {Array.from({ length: Math.ceil(timelineDuration / 1000) + 1 }, (_, i) => (
            <div
              key={i}
              className="absolute top-0 h-full border-l border-gray-600 text-xs text-gray-400 pl-2 flex items-center"
              style={{ left: `${i * pixelsPerSecond}px` }}
            >
              {i}s
            </div>
          ))}
          
          {/* Playhead */}
          <div
            className="absolute top-0 w-0.5 h-full bg-editor-accent shadow-lg z-10"
            style={{ left: `${(currentTime / 1000) * pixelsPerSecond}px` }}
          >
            <div className="w-3 h-3 bg-editor-accent rounded-full -translate-x-1 -translate-y-1" />
          </div>
        </div>
      </div>

      {/* Timeline Tracks */}
      <div className="flex-1 overflow-y-auto">
        {/* RRWeb Sessions Track */}
        {rrwebSessions.length > 0 && (
          <TimelineTrack
            title="Screen Recordings"
            elements={rrwebSessions.map(session => ({
              id: session.id,
              name: `Session ${session.order_in_project || 1}`,
              type: 'rrweb',
              startTime: 0, // Mock data
              duration: 5000, // Mock data
              color: 'bg-blue-600'
            }))}
            pixelsPerSecond={pixelsPerSecond}
            onElementDrag={handleElementDrag}
          />
        )}

        {/* Slides Track */}
        {slides.length > 0 && (
          <TimelineTrack
            title="Slides"
            elements={slides.map((slide, index) => ({
              id: slide.id,
              name: slide.title || `Slide ${index + 1}`,
              type: 'slide',
              startTime: index * 2000, // Mock data
              duration: 2000, // Mock data
              color: 'bg-green-600'
            }))}
            pixelsPerSecond={pixelsPerSecond}
            onElementDrag={handleElementDrag}
          />
        )}

        {/* Audio Track */}
        {audioElements.length > 0 && (
          <TimelineTrack
            title="Audio"
            elements={audioElements.map((audio, index) => ({
              id: audio.id,
              name: audio.name || `Audio ${index + 1}`,
              type: 'audio',
              startTime: 0, // Mock data
              duration: audio.duration_ms || 3000, // Use actual duration if available
              color: 'bg-purple-600'
            }))}
            pixelsPerSecond={pixelsPerSecond}
            onElementDrag={handleElementDrag}
          />
        )}

        {/* Empty State */}
        {rrwebSessions.length === 0 && slides.length === 0 && audioElements.length === 0 && (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            <div className="text-center">
              <div className="text-2xl mb-2">📽️</div>
              <p>No timeline elements yet</p>
              <p className="text-xs mt-1">Add recordings, slides, or audio to start building your video</p>
            </div>
          </div>
        )}
      </div>

      {/* Zoom Controls */}
      <div className="h-8 bg-editor-medium border-t border-editor-light flex items-center justify-end px-4 space-x-2">
        <button
          onClick={() => setZoom(Math.max(0.25, zoom - 0.25))}
          className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded"
        >
          -
        </button>
        <span className="text-xs text-gray-400 min-w-12 text-center">
          {Math.round(zoom * 100)}%
        </span>
        <button
          onClick={() => setZoom(Math.min(4, zoom + 0.25))}
          className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded"
        >
          +
        </button>
      </div>
    </div>
  );
};

export default TimelineEditor;
